import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ArrowLeft, ArrowRight, Home, Info, Monitor } from 'lucide-react'

export interface KeyboardShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
}

interface Shortcut {
  key: string
  description: string
  icon: React.ReactNode
}

const shortcuts: Shortcut[] = [
  { key: '→ or Space', description: 'Next slide', icon: <ArrowRight className="w-5 h-5" /> },
  { key: '←', description: 'Previous slide', icon: <ArrowLeft className="w-5 h-5" /> },
  { key: 'Home', description: 'First slide', icon: <Home className="w-5 h-5" /> },
  { key: 'End', description: 'Last slide', icon: <Info className="w-5 h-5" /> },
  { key: 'F', description: 'Toggle fullscreen', icon: <span className="text-lg">⛶</span> },
  { key: 'T', description: 'Toggle thumbnails', icon: <span className="text-lg">◰</span> },
  { key: 'P', description: 'Enter presenter mode', icon: <Monitor className="w-5 h-5" /> },
  { key: 'E', description: 'Export presentation', icon: <span className="text-lg">⬇</span> },
  { key: '?', description: 'Show/hide shortcuts', icon: <span className="text-lg">?</span> },
  { key: 'Esc', description: 'Exit fullscreen, presenter mode, or go back', icon: <span className="text-lg">⎋</span> },
]

const presenterShortcuts: Shortcut[] = [
  { key: '→ or Space', description: 'Next slide', icon: <ArrowRight className="w-5 h-5" /> },
  { key: '←', description: 'Previous slide', icon: <ArrowLeft className="w-5 h-5" /> },
  { key: 'N', description: 'Toggle notes panel', icon: <span className="text-lg">📝</span> },
  { key: 'X', description: 'Toggle next slide preview', icon: <span className="text-lg">➜</span> },
  { key: 'C', description: 'Toggle timer display', icon: <span className="text-lg">⏱</span> },
  { key: 'R', description: 'Pause/resume timer', icon: <span className="text-lg">⏯</span> },
  { key: 'Shift+R', description: 'Reset timer', icon: <span className="text-lg">↺</span> },
  { key: 'Esc', description: 'Exit presenter mode', icon: <span className="text-lg">⎋</span> },
]

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    Keyboard Shortcuts
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Navigate presentations with ease
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  aria-label="Close shortcuts"
                >
                  <X className="w-6 h-6 text-muted-foreground" />
                </button>
              </div>

              {/* Shortcuts List */}
              <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
                {/* Regular Presentation Shortcuts */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Presentation View
                  </h3>
                  <div className="space-y-3">
                    {shortcuts.map((shortcut, index) => (
                      <motion.div
                        key={`regular-${index}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center justify-between p-4 bg-background rounded-xl hover:bg-card transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                            {shortcut.icon}
                          </div>
                          <span className="font-medium text-foreground">
                            {shortcut.description}
                          </span>
                        </div>
                        <kbd className="px-3 py-1.5 bg-card border-2 border-border rounded-lg text-sm font-mono font-semibold text-muted-foreground shadow-sm">
                          {shortcut.key}
                        </kbd>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Presenter Mode Shortcuts */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Presenter Mode
                  </h3>
                  <div className="space-y-3">
                    {presenterShortcuts.map((shortcut, index) => (
                      <motion.div
                        key={`presenter-${index}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (index + shortcuts.length) * 0.03 }}
                        className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                            {shortcut.icon}
                          </div>
                          <span className="font-medium text-foreground">
                            {shortcut.description}
                          </span>
                        </div>
                        <kbd className="px-3 py-1.5 bg-card border-2 border-purple-200 dark:border-purple-700 rounded-lg text-sm font-mono font-semibold text-muted-foreground shadow-sm">
                          {shortcut.key}
                        </kbd>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 bg-background border-t border-border">
                <p className="text-sm text-center text-muted-foreground">
                  Press <kbd className="px-2 py-1 bg-card border border-border rounded text-xs font-mono">?</kbd> anytime to show or hide this menu
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
